import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Copyright from "../components/CopyRight";
import * as React from "react";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Title from '../components/Title';
import { useEffect, useMemo, useState } from "react";
import CopyToClipboard from "../components/CopyToClipboard";
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Snackbar, Stack, TextField } from "@mui/material";
import { useCompany } from "../hooks/useCompany";
import { Geography } from "../models/DataSource";
import geographyData from '../assets/data/geography.json';
import { APIEndpoint, EndpointType, httpPut } from "../utils/apiService";

import CloseIcon from "@mui/icons-material/Close";
import { useCompanies } from "../hooks/useCompanies";
import { useUser } from "../hooks/useUser";
import { CompaniesResponse } from "../models/CompaniesResponse";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const window: any; // Needed for Hubspot


const Company = () => {
  const [geography, setGeography] = useState<Geography>({ state: {}, county: {}, metro_area: {}, scf: {} });
  const companyRoles = useMemo(() => [], []);

  const [companyID, setCompanyID] = useState<string>("");
  const [billingEmail, setBillingEmail] = useState("");
  const [name, setName] = React.useState("");
  const [storefrontName, setStorefrontName] = React.useState("");
  const [address1, setAddress1] = React.useState("");
  const [address2, setAddress2] = React.useState("");
  const [roles, setRoles] = React.useState<string[]>([]);
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [zip, setZip] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [mainContactUserID, setMainContactUserID] = React.useState("");
  const [websiteURL, setWebsiteURL] = React.useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [snackbarMsg, setSnackbarMsg] = useState("")
  const [companyRefreshToken, setCompanyRefreshToken] = useState(1);

  const { companies } = useCompanies(companyRoles,companyRefreshToken);
  const { company } = useCompany(companyID);
  const { customer } = useUser(mainContactUserID)

  useEffect(() => {
    // Log Pageview with Hubspot
    const _hsq = window._hsq = window._hsq || [];
    _hsq.push(['setPath', '/profile']);
    _hsq.push(['trackPageView']);

    // Load geography data 
    setGeography(geographyData as Geography);
  }, []);

  useEffect(() => {
    // Set default values from company context
    if (company) {
      setName(company.name || "");
      setStorefrontName(company.storefrontName || "");
      setAddress1(company.companyAddress.address1 || "");
      setAddress2(company.companyAddress.address2 || "");
      setCity(company.companyAddress.city || "");
      setState(company.companyAddress.state || "");
      setZip(company.companyAddress.zip || "");
      setPhone(company.phone || "");
      setBillingEmail(company.billingEmail || "");
      setWebsiteURL(company.websiteURL || "");
      setRoles(company.roles || []);
      setMainContactUserID(company.mainContactUserID || "");
    }
  }, [company]);

  const saveChanges = () => {
    if (!company) {
      setSnackbarMsg("No company selected");
      setOpenSnackbar(true);
      return;
    }

    const newCompany = company
    newCompany.name = name;
    newCompany.storefrontName = storefrontName;
    newCompany.companyAddress.address1 = address1;
    newCompany.companyAddress.address2 = address2;
    newCompany.companyAddress.city = city;
    newCompany.companyAddress.state = state;
    newCompany.companyAddress.zip = zip;
    newCompany.phone = phone;
    newCompany.billingEmail = billingEmail;
    newCompany.websiteURL = websiteURL;

    const apiURL = APIEndpoint(EndpointType.Companies) + "/" + newCompany.id;
    httpPut(apiURL, JSON.stringify(newCompany))
      .then((data) => {
        const response = data as CompaniesResponse;

        if (response.status == 'error') {
          setSnackbarMsg(`Error updating company: ${response.errorMessage}`);
          setOpenSnackbar(true);
        } else {
          setSnackbarMsg("Company updated successfully")
          setOpenSnackbar(true)
          setCompanyRefreshToken(companyRefreshToken + 1); // Trigger refresh of company list
        }
      })
      .catch((error) => {
        setSnackbarMsg(`Company was not saved - error: ${error as string}`)
        setOpenSnackbar(true)
      });
  }

  const getFriendlyRoles = (roles: string[]) => {
    if (!roles || roles.length === 0) {
      return "No roles assigned";
    }

    return roles.map(role => {
      switch (role) {
        case 'query_api':
          return 'API Access';
        case 'storefront':
          return 'Custom Storefront';
        case 'orders':
          return 'View Storefront Orders';
        default:
          return role.charAt(0).toUpperCase() + role.slice(1);
      }
    }).join(', ');
  };

  const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  const getPrimaryContactDisplay = () => {
    if (!customer) {
      return "No primary contact assigned";
    }

    return `${customer.firstName} ${customer.lastName} (${customer.email})`;
  };

  const snackbarAction = (
    <React.Fragment>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleSnackbarClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
            {/* Search */}
            <Title>Company Profile</Title>
            <Grid item xs={12}>
              <FormControl size="medium" sx={{ width: 300, height: 70, mr: 1 }}>
                  <InputLabel id="company-list-label">Company</InputLabel>
                  <Select labelId="company-list" label="Company" onChange={(event) => setCompanyID(event.target.value as string)} size="medium" value={companyID}>
                      {companies && companies.map((company) => (
                          <MenuItem value={company.id} key={company.id}>{company.name}</MenuItem>
                      ))}
                  </Select>
              </FormControl>
              <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
                <Stack spacing={2} alignItems="left">
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
                    <TextField id="firstName" label="Company Name" value={name} sx={{ width: 300 }} onChange={(e) => setName(e.target.value)}></TextField>
                    <TextField id="storefrontName" label="Storefront Name" value={storefrontName} sx={{ width: 300 }} onChange={(e) => setStorefrontName(e.target.value)}></TextField>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
                    <TextField id="phone" label="Phone" value={phone} sx={{ width: 300, mt: 2, mb: 1 }} onChange={(e) => setPhone(e.target.value)} ></TextField>
                    <TextField id="email" label="Billing/Reporting Email" value={billingEmail} sx={{ width: 400, mt: 2, mb: 1 }} onChange={(e) => setBillingEmail(e.target.value)}></TextField>
                  </Stack>
                  <Box >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0, mb: 1 }}>
                      <TextField id="address1" label="Address" value={address1} sx={{ width: 400, mt: 0, mb: 1 }} onChange={(e) => setAddress1(e.target.value)}></TextField>
                      <TextField id="address2" label="Address 2" value={address2} sx={{ width: 400, mt: 0, mb: 1 }} onChange={(e) => setAddress2(e.target.value)}></TextField>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                      <TextField id="city" label="City" value={city} sx={{ width: 300 }} onChange={(e) => setCity(e.target.value)}></TextField>
                      <FormControl fullWidth size="small" sx={{ width: 200 }}>
                        <InputLabel>State</InputLabel>
                        <Select label="Values" onChange={(e) => setState(e.target.value)} size="medium" value={state} MenuProps={{
                        }}>
                          {Object.entries(geography.state).map(([key, value]) => (
                            <MenuItem value={key} key={key}>{value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField id="zip" label="Zip" value={zip} sx={{ width: 100 }} onChange={(e) => setZip(e.target.value)}></TextField>
                    </Stack>
                    <TextField id="websiteUrl" label="Website URL" value={websiteURL} sx={{ width: 400, mt: 2, mb: 1 }} onChange={(e) => setWebsiteURL(e.target.value)}></TextField>
                  </Box>
                </Stack>
                <Table size="small" sx={{ mt: 2 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell>Features:</TableCell>
                      <TableCell>
                        {getFriendlyRoles(roles)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Primary Contact</TableCell>
                      <TableCell>
                        {getPrimaryContactDisplay()}<CopyToClipboard value={customer && customer?.id} /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Grid item xs={3}>
                  <Button variant="contained" color="primary" sx={{ mt: 3, ml: 1 }} onClick={saveChanges}>
                    Save Changes
                  </Button>
                </Grid>
              </Paper>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Copyright sx={{ pt: 4 }} />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMsg}
        action={snackbarAction}
      />
    </Container>
  );
};

export default Company;